import React, { useState, useEffect, useRef } from 'react';
import { X, Route, Navigation, Save, RotateCcw, MapPin, Edit3, Check, AlertCircle, Plus, Trash2 } from 'lucide-react';

const RouteModal = ({ isOpen, onClose, origin, destination, originLocation = null, destinationLocation = null, waypoints: initialWaypoints = [], googleMapsLoaded, onSave }) => {
  const mapRef = useRef(null);
  const [routeData, setRouteData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [map, setMap] = useState(null);
  const [directionsService, setDirectionsService] = useState(null);
  const [directionsRenderer, setDirectionsRenderer] = useState(null);

  // Interactive functionality state
  const [currentOrigin, setCurrentOrigin] = useState(origin);
  const [currentDestination, setCurrentDestination] = useState(destination);
  // When provided by the parent (e.g. the geocoded Lieferwerk/Baustelle
  // coordinate), this is the source of truth for the routing point — we skip
  // re-geocoding the label and pass the coords straight to DirectionsService.
  const [currentOriginLocation, setCurrentOriginLocation] = useState(originLocation);
  const [currentDestinationLocation, setCurrentDestinationLocation] = useState(destinationLocation);
  const [originMarker, setOriginMarker] = useState(null);
  const [destinationMarker, setDestinationMarker] = useState(null);

  // Waypoint functionality state
  const [waypoints, setWaypoints] = useState([]);
  const [waypointMarkers, setWaypointMarkers] = useState([]);
  const [isAddingWaypoint, setIsAddingWaypoint] = useState(false);
  const [mapClickListener, setMapClickListener] = useState(null);

  const [hasChanges, setHasChanges] = useState(false);
  const [geocoder, setGeocoder] = useState(null);

  // Form state for manual address input
  const [formOrigin, setFormOrigin] = useState(origin);
  const [formDestination, setFormDestination] = useState(destination);
  const [formWaypoints, setFormWaypoints] = useState([]);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Initialize Google Maps components when modal opens
  useEffect(() => {
    if (isOpen && googleMapsLoaded && mapRef.current && !map) {
      console.log('Initializing Google Maps...', { isOpen, googleMapsLoaded, mapRef: !!mapRef.current, map: !!map });
      try {
        const mapInstance = new window.google.maps.Map(mapRef.current, {
          zoom: 10,
          center: { lat: 49.4521, lng: 11.0767 }, // Default center (Germany)
          mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        });

        const directionsServiceInstance = new window.google.maps.DirectionsService();
        const directionsRendererInstance = new window.google.maps.DirectionsRenderer({
          draggable: false,
          panel: null,
          suppressMarkers: true, // We'll use custom markers
        });

        const geocoderInstance = new window.google.maps.Geocoder();

        directionsRendererInstance.setMap(mapInstance);





        setMap(mapInstance);
        setDirectionsService(directionsServiceInstance);
        setDirectionsRenderer(directionsRendererInstance);
        setGeocoder(geocoderInstance);
        console.log('Google Maps initialized successfully');
      } catch (error) {
        console.error('Error initializing Google Maps:', error);
        setError('Karte konnte nicht initialisiert werden');
      }
    }
  }, [isOpen, googleMapsLoaded, map]);

  // Update origin marker
  const updateOriginMarker = (latLng, address) => {
    // Remove existing marker if it exists
    if (originMarker) {
      originMarker.setMap(null);
    }

    const marker = new window.google.maps.Marker({
      position: latLng,
      map: map,
      title: `Start: ${address}`,
      icon: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
            <circle cx="16" cy="16" r="12" fill="#10B981" stroke="white" stroke-width="3"/>
            <text x="16" y="21" text-anchor="middle" fill="white" font-family="Arial" font-size="14" font-weight="bold">A</text>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(32, 32),
        anchor: new window.google.maps.Point(16, 16),
      },
      draggable: true,
    });

    // Add drag listener
    marker.addListener('dragend', (event) => {
      handleMarkerDrag(event.latLng, 'origin');
    });

    setOriginMarker(marker);
  };

  // Update destination marker
  const updateDestinationMarker = (latLng, address) => {
    // Remove existing marker if it exists
    if (destinationMarker) {
      destinationMarker.setMap(null);
    }

    const marker = new window.google.maps.Marker({
      position: latLng,
      map: map,
      title: `Ziel: ${address}`,
      icon: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
            <circle cx="16" cy="16" r="12" fill="#EF4444" stroke="white" stroke-width="3"/>
            <text x="16" y="21" text-anchor="middle" fill="white" font-family="Arial" font-size="14" font-weight="bold">B</text>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(32, 32),
        anchor: new window.google.maps.Point(16, 16),
      },
      draggable: true,
    });

    // Add drag listener
    marker.addListener('dragend', (event) => {
      handleMarkerDrag(event.latLng, 'destination');
    });

    setDestinationMarker(marker);
  };

  // Helper function to create waypoint marker
  const createWaypointMarker = (location, address, index) => {
    const marker = new window.google.maps.Marker({
      position: location,
      map: map,
      title: `Wegpunkt ${index + 1}: ${address}`,
      draggable: true,
      icon: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="8" fill="#f59e0b" stroke="white" stroke-width="2"/>
            <text x="12" y="16" text-anchor="middle" fill="white" font-family="Arial" font-size="10" font-weight="bold">${index + 1}</text>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(24, 24),
        anchor: new window.google.maps.Point(12, 12),
      },
    });

    marker.addListener('dragend', (event) => {
      handleWaypointDrag(event.latLng, index);
    });

    // Add right-click context menu to remove waypoint
    marker.addListener('rightclick', () => {
      removeWaypoint(index);
    });

    return marker;
  };

  // Handle waypoint drag
  const handleWaypointDrag = (latLng, index) => {
    if (!geocoder) return;

    const lat = latLng.lat();
    const lng = latLng.lng();

    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const address = results[0].formatted_address;

        setWaypoints(prev => {
          const newWaypoints = [...prev];
          newWaypoints[index] = {
            ...newWaypoints[index],
            address,
            location: latLng
          };
          return newWaypoints;
        });

        setFormWaypoints(prev => {
          const newFormWaypoints = [...prev];
          newFormWaypoints[index] = address;
          return newFormWaypoints;
        });

        // Update marker title
        if (waypointMarkers[index]) {
          waypointMarkers[index].setTitle(`Wegpunkt ${index + 1}: ${address}`);
        }

        setHasChanges(true);
      }
    });
  };

  // Add waypoint function
  const addWaypoint = (address = '') => {
    setFormWaypoints(prev => [...prev, address]);
    if (address) {
      // If address is provided, geocode it
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const newWaypoint = {
            address,
            location: results[0].geometry.location
          };
          setWaypoints(prev => [...prev, newWaypoint]);
          setHasChanges(true);
        }
      });
    }
  };

  // Handle waypoint button click - both enable map mode and add empty field
  const handleWaypointButtonClick = () => {
    if (isAddingWaypoint) {
      // Cancel adding waypoint
      setIsAddingWaypoint(false);
    } else {
      // Enable map clicking mode AND add empty input field
      setIsAddingWaypoint(true);
      addWaypoint(''); // Add empty waypoint field
    }
  };

  // Remove waypoint function
  const removeWaypoint = (index) => {
    setWaypoints(prev => prev.filter((_, i) => i !== index));
    setFormWaypoints(prev => prev.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  // Update waypoint markers when waypoints change
  useEffect(() => {
    if (map) {
      // Clear existing waypoint markers
      waypointMarkers.forEach(marker => marker.setMap(null));

      if (waypoints.length > 0) {
        // Create new waypoint markers
        const newMarkers = waypoints.map((waypoint, index) =>
          createWaypointMarker(waypoint.location, waypoint.address, index)
        );

        setWaypointMarkers(newMarkers);
      } else {
        // No waypoints, clear the markers array
        setWaypointMarkers([]);
      }
    }
  }, [map, waypoints]);

  // Handle map click listener for adding waypoints
  useEffect(() => {
    if (map && geocoder) {
      // Remove existing listener if it exists
      if (mapClickListener) {
        window.google.maps.event.removeListener(mapClickListener);
      }

      // Add new listener
      const listener = map.addListener('click', (event) => {
        console.log('Map clicked, isAddingWaypoint:', isAddingWaypoint);
        if (isAddingWaypoint) {
          const latLng = event.latLng;
          console.log('Adding waypoint at:', latLng.lat(), latLng.lng());

          // Geocode the clicked location
          geocoder.geocode({ location: latLng }, (results, status) => {
            if (status === 'OK' && results[0]) {
              const address = results[0].formatted_address;
              const newWaypoint = {
                address,
                location: latLng
              };

              console.log('Adding waypoint:', newWaypoint);

              // Check if there's an empty waypoint to replace
              const emptyIndex = formWaypoints.findIndex(wp => wp.trim() === '');
              if (emptyIndex !== -1) {
                // Replace the empty waypoint
                setFormWaypoints(prev => {
                  const newFormWaypoints = [...prev];
                  newFormWaypoints[emptyIndex] = address;
                  return newFormWaypoints;
                });
                setWaypoints(prev => {
                  const newWaypoints = [...prev];
                  newWaypoints[emptyIndex] = newWaypoint;
                  return newWaypoints;
                });
              } else {
                // No empty waypoint, add new one
                setWaypoints(prev => [...prev, newWaypoint]);
                setFormWaypoints(prev => [...prev, address]);
              }

              setHasChanges(true);
              setIsAddingWaypoint(false);
            } else {
              console.error('Geocoding failed:', status);
            }
          });
        }
      });

      setMapClickListener(listener);
    }

    // Cleanup function
    return () => {
      if (mapClickListener) {
        window.google.maps.event.removeListener(mapClickListener);
      }
    };
  }, [map, geocoder, isAddingWaypoint]);

  // Handle marker drag
  const handleMarkerDrag = (latLng, type) => {
    if (!geocoder) return;

    const lat = latLng.lat();
    const lng = latLng.lng();

    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const address = results[0].formatted_address;

        if (type === 'origin') {
          setCurrentOrigin(address);
          setFormOrigin(address); // Keep form in sync
          // Drag updates the coord-based source of truth so DirectionsService
          // routes from the user's pin, not the label's geocode.
          setCurrentOriginLocation({ lat, lng });
          // Update the marker title without recreating it
          if (originMarker) {
            originMarker.setTitle(`Origin: ${address}`);
          }
        } else {
          setCurrentDestination(address);
          setFormDestination(address); // Keep form in sync
          // Drag updates the coord-based source of truth so DirectionsService
          // routes to the user's pin, not the label's geocode.
          setCurrentDestinationLocation({ lat, lng });
          // Update the marker title without recreating it
          if (destinationMarker) {
            destinationMarker.setTitle(`Destination: ${address}`);
          }
        }

        setHasChanges(true);
      }
    });
  };

  // Initialize markers when addresses are available
  useEffect(() => {
    if (map && geocoder && currentOrigin && !originMarker) {
      if (currentOriginLocation) {
        updateOriginMarker(currentOriginLocation, currentOrigin);
      } else {
        geocoder.geocode({ address: currentOrigin }, (results, status) => {
          if (status === 'OK' && results[0] && !originMarker) {
            updateOriginMarker(results[0].geometry.location, currentOrigin);
          }
        });
      }
    }
  }, [map, geocoder, currentOrigin, currentOriginLocation, originMarker]);

  useEffect(() => {
    if (map && geocoder && currentDestination && !destinationMarker) {
      if (currentDestinationLocation) {
        // Source of truth from parent (e.g. geocoded Lieferwerk) — use coords directly
        updateDestinationMarker(currentDestinationLocation, currentDestination);
      } else {
        // Fallback: geocode the label
        geocoder.geocode({ address: currentDestination }, (results, status) => {
          if (status === 'OK' && results[0] && !destinationMarker) {
            updateDestinationMarker(results[0].geometry.location, currentDestination);
          }
        });
      }
    }
  }, [map, geocoder, currentDestination, currentDestinationLocation, destinationMarker]);

  // Calculate and display route when origin/destination/waypoints change
  useEffect(() => {
    const calculateRoute = async () => {
      if (!currentOrigin || !currentDestination || !directionsService || !directionsRenderer) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const request = {
          origin: currentOriginLocation || currentOrigin,
          destination: currentDestinationLocation || currentDestination,
          travelMode: window.google.maps.TravelMode.DRIVING,
          unitSystem: window.google.maps.UnitSystem.METRIC,
        };

        // Add waypoints if they exist and have valid locations
        const validWaypoints = waypoints.filter(waypoint =>
          waypoint &&
          waypoint.location &&
          waypoint.address &&
          waypoint.address.trim() !== ''
        );

        if (validWaypoints.length > 0) {
          request.waypoints = validWaypoints.map(waypoint => ({
            location: waypoint.location,
            stopover: true
          }));
        }

        directionsService.route(request, (result, status) => {
          if (status === window.google.maps.DirectionsStatus.OK) {
            directionsRenderer.setDirections(result);

            // Extract route information
            const route = result.routes[0];

            // Calculate total distance and duration
            let totalDistance = 0;
            let totalDuration = 0;
            const legs = [];

            route.legs.forEach((leg) => {
              totalDistance += leg.distance.value;
              totalDuration += leg.duration.value;

              legs.push({
                distance: leg.distance.text,
                duration: leg.duration.text,
                startAddress: leg.start_address,
                endAddress: leg.end_address,
                steps: leg.steps.map(step => ({
                  instruction: step.instructions.replace(/<[^>]*>/g, ''), // Remove HTML tags
                  distance: step.distance.text,
                  duration: step.duration.text,
                }))
              });
            });

            // Format total distance and duration
            const totalDistanceText = totalDistance >= 1000
              ? `${(totalDistance / 1000).toFixed(1)} km`
              : `${totalDistance} m`;

            const totalDurationText = totalDuration >= 3600
              ? `${Math.floor(totalDuration / 3600)}h ${Math.floor((totalDuration % 3600) / 60)}m`
              : `${Math.floor(totalDuration / 60)}m`;

            setRouteData({
              distance: totalDistanceText,
              duration: totalDurationText,
              startAddress: route.legs[0].start_address,
              endAddress: route.legs[route.legs.length - 1].end_address,
              legs: legs,
              steps: legs.flatMap(leg => leg.steps) // Flatten all steps for backward compatibility
            });
          } else {
            setError(`Routenberechnung fehlgeschlagen: ${status}`);
          }
          setLoading(false);
        });
      } catch (error) {
        console.error('Error calculating route:', error);
        setError('Route konnte nicht berechnet werden');
        setLoading(false);
      }
    };

    calculateRoute();
  }, [currentOrigin, currentDestination, currentOriginLocation, currentDestinationLocation, waypoints, directionsService, directionsRenderer]);

  // Reset to original values
  const handleReset = () => {
    // Clear existing markers first
    if (originMarker) {
      originMarker.setMap(null);
      setOriginMarker(null);
    }
    if (destinationMarker) {
      destinationMarker.setMap(null);
      setDestinationMarker(null);
    }

    // Clear waypoint markers
    waypointMarkers.forEach(marker => marker.setMap(null));
    setWaypointMarkers([]);

    // Reset addresses and waypoints - this will trigger marker recreation via useEffect
    setCurrentOrigin(origin);
    setCurrentDestination(destination);
    setCurrentOriginLocation(originLocation);
    setCurrentDestinationLocation(destinationLocation);
    setFormOrigin(origin);
    setFormDestination(destination);
    setWaypoints(initialWaypoints || []);
    setFormWaypoints((initialWaypoints || []).map(wp => wp.address || ''));
    setHasChanges(false);
    setFormError(null);
    setValidationErrors({});
  };

  // Helper function to extract numeric distance from Google Maps distance text
  const extractNumericDistance = (distanceText) => {
    if (!distanceText) return null;

    // Extract numeric value from text like "15.2 km" or "1,234 m"
    const match = distanceText.match(/([0-9,]+\.?[0-9]*)/);
    if (!match) return null;

    const numericValue = parseFloat(match[1].replace(',', ''));

    // Convert to kilometers if the text contains 'm' (meters)
    if (distanceText.toLowerCase().includes(' m') && !distanceText.toLowerCase().includes('km')) {
      return Math.round((numericValue / 1000) * 10) / 10; // Convert meters to km, round to 1 decimal
    }

    // Already in kilometers, round to 1 decimal place
    return Math.round(numericValue * 10) / 10;
  };

  // Save changes and call parent callback with updated distance
  const handleSave = () => {
    setHasChanges(false);

    // Extract numeric distance and call parent callback if provided
    if (onSave && routeData && routeData.distance) {
      const numericDistance = extractNumericDistance(routeData.distance);

      console.log('Saving route changes:', {
        distance: numericDistance,
        distanceText: routeData.distance
      });

      onSave({
        origin: currentOrigin,
        destination: currentDestination,
        waypoints: waypoints,
        distance: numericDistance,
        distanceText: routeData.distance
      });
    }
  };

  // Handle manual address form submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!geocoder || !formOrigin.trim() || !formDestination.trim()) return;

    setFormLoading(true);
    setFormError(null);
    setValidationErrors({});

    try {
      // Validate and geocode origin and destination addresses
      const originPromise = new Promise((resolve, reject) => {
        geocoder.geocode({ address: formOrigin.trim() }, (results, status) => {
          if (status === 'OK' && results[0]) {
            resolve({ address: formOrigin.trim(), location: results[0].geometry.location });
          } else {
            reject(new Error(`Origin address not found: ${formOrigin}`));
          }
        });
      });

      const destinationPromise = new Promise((resolve, reject) => {
        geocoder.geocode({ address: formDestination.trim() }, (results, status) => {
          if (status === 'OK' && results[0]) {
            resolve({ address: formDestination.trim(), location: results[0].geometry.location });
          } else {
            reject(new Error(`Destination address not found: ${formDestination}`));
          }
        });
      });

      // Validate and geocode waypoints
      const waypointPromises = formWaypoints
        .filter(waypoint => waypoint.trim())
        .map((waypoint, index) =>
          new Promise((resolve, reject) => {
            geocoder.geocode({ address: waypoint.trim() }, (results, status) => {
              if (status === 'OK' && results[0]) {
                resolve({ address: waypoint.trim(), location: results[0].geometry.location });
              } else {
                reject(new Error(`Waypoint ${index + 1} address not found: ${waypoint}`));
              }
            });
          })
        );

      const [originResult, destinationResult, ...waypointResults] = await Promise.all([
        originPromise,
        destinationPromise,
        ...waypointPromises
      ]);

      // Clear existing markers
      if (originMarker) {
        originMarker.setMap(null);
        setOriginMarker(null);
      }
      if (destinationMarker) {
        destinationMarker.setMap(null);
        setDestinationMarker(null);
      }

      // Clear waypoint markers
      waypointMarkers.forEach(marker => marker.setMap(null));
      setWaypointMarkers([]);

      // Update addresses and create new markers. Adopt the freshly geocoded
      // coords as the new source of truth so DirectionsService routes through
      // the same points the markers display.
      setCurrentOrigin(originResult.address);
      setCurrentDestination(destinationResult.address);
      const toLatLng = (loc) => ({
        lat: typeof loc.lat === 'function' ? loc.lat() : loc.lat,
        lng: typeof loc.lng === 'function' ? loc.lng() : loc.lng,
      });
      setCurrentOriginLocation(toLatLng(originResult.location));
      setCurrentDestinationLocation(toLatLng(destinationResult.location));
      updateOriginMarker(originResult.location, originResult.address);
      updateDestinationMarker(destinationResult.location, destinationResult.address);

      // Update waypoints
      setWaypoints(waypointResults);

      setHasChanges(true);
      setFormLoading(false);

    } catch (error) {
      console.error('Geocoding error:', error);
      setFormError(error.message);

      // Set specific field errors
      const errors = {};
      if (error.message.includes('Origin')) {
        errors.origin = 'Address not found';
      }
      if (error.message.includes('Destination')) {
        errors.destination = 'Address not found';
      }
      setValidationErrors(errors);
      setFormLoading(false);
    }
  };

  // Initialize current values when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentOrigin(origin);
      setCurrentDestination(destination);
      setCurrentOriginLocation(originLocation);
      setCurrentDestinationLocation(destinationLocation);
      setFormOrigin(origin);
      setFormDestination(destination);
      setWaypoints(initialWaypoints || []);
      setFormWaypoints((initialWaypoints || []).map(wp => wp.address || ''));
      setHasChanges(false);
      setFormError(null);
      setValidationErrors({});
    }
  }, [isOpen, origin, destination, originLocation, destinationLocation, initialWaypoints]);

  // Clean up when modal closes
  useEffect(() => {
    if (!isOpen) {
      setRouteData(null);
      setError(null);
      setLoading(false);
      // Reset map state to force re-initialization
      setMap(null);
      setDirectionsService(null);
      setDirectionsRenderer(null);
      setGeocoder(null);

      // Clear markers
      if (originMarker) {
        originMarker.setMap(null);
        setOriginMarker(null);
      }
      if (destinationMarker) {
        destinationMarker.setMap(null);
        setDestinationMarker(null);
      }

      // Clear waypoint markers
      waypointMarkers.forEach(marker => marker.setMap(null));
      setWaypointMarkers([]);
      setWaypoints([]);
      setFormWaypoints([]);

      // Remove map click listener
      if (mapClickListener) {
        window.google.maps.event.removeListener(mapClickListener);
        setMapClickListener(null);
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center flex-1">
            <Route className="h-6 w-6 text-blue-500 mr-3" />
            <div className="flex-1">
              <div className="flex items-center gap-4">
                <h2 className="text-xl font-semibold">Routenvisualisierung</h2>
                {hasChanges && (
                  <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                    Geändert
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-600 mt-1">
                <div className="flex items-center mb-1">
                  <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                  <span className="font-medium">Von:</span>
                  <span className="ml-1 truncate max-w-md">{currentOrigin}</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                  <span className="font-medium">Nach:</span>
                  <span className="ml-1 truncate max-w-md">{currentDestination}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Route Editing Controls */}
          <div className="flex items-center gap-2 mr-4">
            <div className="flex items-center bg-gray-50 rounded-lg px-3 py-2">
              <span className="text-sm font-medium text-gray-700 mr-3">Route bearbeiten</span>
              <button
                onClick={handleWaypointButtonClick}
                className={`text-xs px-2 py-1 rounded-md transition-colors flex items-center gap-1 ${
                  isAddingWaypoint
                    ? 'bg-orange-500 text-white hover:bg-orange-600'
                    : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                }`}
                title={isAddingWaypoint ? 'Wegpunkt hinzufügen abbrechen' : 'Wegpunkt hinzufügen - Eingabefeld und Kartenklick'}
              >
                <Plus className="h-3 w-3" />
                {isAddingWaypoint ? 'Abbrechen' : 'Wegpunkt'}
              </button>
            </div>
          </div>

          {/* Interactive Controls */}
          {hasChanges && (
            <div className="flex items-center gap-2 mr-4">
              <button
                onClick={handleReset}
                className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors flex items-center gap-1"
              >
                <RotateCcw className="h-3 w-3" />
                Zurücksetzen
              </button>
              <button
                onClick={handleSave}
                className="px-3 py-1 text-xs bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors flex items-center gap-1"
              >
                <Save className="h-3 w-3" />
                Speichern
              </button>
            </div>
          )}

          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Dynamic Contextual Instructions */}
        <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50 to-gray-50 px-6 py-3">
          <div className="flex flex-wrap gap-2">
            {/* Dynamic waypoint instructions based on state */}
            {isAddingWaypoint ? (
              // When actively adding a waypoint
              <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-orange-500 text-white shadow-sm">
                <span className="mr-1">📍</span>
                Klicken Sie auf die Karte um einen Wegpunkt hinzuzufügen
              </span>
            ) : waypoints.length > 0 ? (
              // When waypoints exist - show management options
              <>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
                  🟠 Standort der Wegpunkte per Drag and Drop ändern
                </span>
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                  🖱️ Rechtsklick auf Wegpunkt um diesen zu entfernen
                </span>
              </>
            ) : null}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 flex">
          {/* Map Container */}
          <div className="flex-1 relative">


            {loading && (
              <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-10">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                  <span className="text-gray-600">Route wird berechnet...</span>
                </div>
              </div>
            )}

            {error && (
              <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-10">
                <div className="text-center text-red-600">
                  <Navigation className="h-8 w-8 mx-auto mb-2" />
                  <div className="font-medium">Routenfehler</div>
                  <div className="text-sm">{error}</div>
                </div>
              </div>
            )}

            <div
              ref={mapRef}
              className={`w-full h-full min-h-[400px] ${isAddingWaypoint ? 'cursor-crosshair' : ''}`}
              style={{ minHeight: '400px' }}
            />
          </div>

          {/* Route Details Sidebar */}
          {routeData && (
            <div className="w-80 border-l border-gray-200 bg-gray-50 overflow-y-auto">
              <div className="p-4">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Navigation className="h-5 w-5 mr-2 text-blue-600" />
                  Routendetails
                </h3>

                {/* Summary */}
                <div className="bg-white rounded-lg p-4 mb-4 border">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{routeData.distance}</div>
                      <div className="text-sm text-gray-600">Entfernung</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{routeData.duration}</div>
                      <div className="text-sm text-gray-600">Dauer</div>
                    </div>
                  </div>
                </div>

                {/* Address Editor Form */}
                <div className="bg-white rounded-lg border">
                  <div className="p-3 border-b border-gray-200">
                    <h4 className="font-medium text-gray-900 flex items-center">
                      <Edit3 className="h-4 w-4 mr-2 text-blue-600" />
                      Adressen bearbeiten
                    </h4>
                  </div>
                  <form onSubmit={handleFormSubmit} className="p-4 space-y-4">
                    {/* Origin Input */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <div className="flex items-center">
                          <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                          Von
                        </div>
                      </label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          value={formOrigin}
                          onChange={(e) => setFormOrigin(e.target.value)}
                          className={`w-full pl-10 pr-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            validationErrors.origin ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Von Adresse eingeben..."
                          disabled={formLoading}
                        />
                      </div>
                      {validationErrors.origin && (
                        <div className="flex items-center mt-1 text-xs text-red-600">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          {validationErrors.origin}
                        </div>
                      )}
                    </div>

                    {/* Destination Input */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <div className="flex items-center">
                          <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                          Nach
                        </div>
                      </label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          value={formDestination}
                          onChange={(e) => setFormDestination(e.target.value)}
                          className={`w-full pl-10 pr-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            validationErrors.destination ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Nach Adresse eingeben..."
                          disabled={formLoading}
                        />
                      </div>
                      {validationErrors.destination && (
                        <div className="flex items-center mt-1 text-xs text-red-600">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          {validationErrors.destination}
                        </div>
                      )}
                    </div>

                    {/* Waypoints Section */}
                    {(formWaypoints.length > 0 || isAddingWaypoint) && (
                      <div>
                        <div className="mb-2">
                          <label className="block text-sm font-medium text-gray-700">
                            <div className="flex items-center">
                              <div className="w-3 h-3 rounded-full bg-orange-500 mr-2"></div>
                              Wegpunkte
                            </div>
                          </label>
                        </div>

                        {formWaypoints.length > 0 && (
                        <div className="space-y-2">
                          {formWaypoints.map((waypoint, index) => (
                            <div key={index} className="flex items-center gap-2">
                              <div className="flex-1 relative">
                                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <input
                                  type="text"
                                  value={waypoint}
                                  onChange={(e) => {
                                    const newWaypoints = [...formWaypoints];
                                    newWaypoints[index] = e.target.value;
                                    setFormWaypoints(newWaypoints);
                                  }}
                                  className={`w-full pl-10 pr-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                    validationErrors[`waypoint${index}`] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                                  }`}
                                  placeholder={`Wegpunkt ${index + 1} Adresse...`}
                                  disabled={formLoading}
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => removeWaypoint(index)}
                                className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                                disabled={formLoading}
                                title="Wegpunkt entfernen"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                        )}
                      </div>
                    )}

                    {/* Form Error */}
                    {formError && (
                      <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-md">
                        <AlertCircle className="h-4 w-4 text-red-500 mr-2 flex-shrink-0" />
                        <span className="text-sm text-red-700">{formError}</span>
                      </div>
                    )}

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={formLoading || !formOrigin.trim() || !formDestination.trim()}
                      className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {formLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Route wird aktualisiert...
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          Route aktualisieren
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RouteModal;
